"use client";

import { notificationService } from "@/services/notification.service";
import { detectPlatform, isStandalone, pushSupported } from "./installDetect";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

export type PushSubscribeResult =
  | { ok: true }
  | { ok: false; reason: "unsupported" | "permission_denied" | "ios_needs_install" | "error"; error?: string };

/**
 * Subscribes the current device to push and registers the subscription on the
 * server. Idempotent — calling twice with the same browser keys upserts.
 *
 * Permission flow:
 *   - if 'denied' → returns permission_denied (user must reset in browser)
 *   - if 'default' → triggers prompt
 *   - if 'granted' → silently subscribes
 */
export async function subscribeToPush(): Promise<PushSubscribeResult> {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  if (detectPlatform() === "ios" && !isStandalone()) {
    return { ok: false, reason: "ios_needs_install" };
  }

  // Ensure SW is registered
  const reg = await navigator.serviceWorker.ready;

  if (Notification.permission === "denied") {
    return { ok: false, reason: "permission_denied" };
  }
  if (Notification.permission === "default") {
    const result = await Notification.requestPermission();
    if (result !== "granted") return { ok: false, reason: "permission_denied" };
  }

  try {
    const vapidPublic = await notificationService.getVapidKey();
    const keyBytes = urlBase64ToUint8Array(vapidPublic);
    console.debug("[push] VAPID key length:", keyBytes.length, "(expected 65)");
    let sub = await reg.pushManager.getSubscription();
    if (sub) {
      const existingKey = sub.options.applicationServerKey;
      const matches =
        existingKey &&
        new Uint8Array(existingKey).every((b, i) => b === keyBytes[i]) &&
        existingKey.byteLength === keyBytes.length;
      if (!matches) {
        console.debug("[push] existing subscription has different VAPID key — unsubscribing");
        await sub.unsubscribe();
        sub = null;
      }
    }
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes.buffer as ArrayBuffer,
      });
    }

    const json = sub.toJSON() as {
      endpoint: string;
      keys?: { p256dh?: string; auth?: string };
    };
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
      return { ok: false, reason: "error", error: "Subscription incomplete" };
    }

    await notificationService.registerPushSubscription({
      endpoint: json.endpoint,
      p256dh: json.keys.p256dh,
      auth: json.keys.auth,
      user_agent: navigator.userAgent,
      is_standalone: isStandalone(),
    });
    return { ok: true };
  } catch (e) {
    console.error("[push] subscribe failed:", e);
    return { ok: false, reason: "error", error: e instanceof Error ? e.message : String(e) };
  }
}

export async function unsubscribeFromPush(): Promise<void> {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) await sub.unsubscribe();
}

/**
 * Re-subscribe transparently when the SW signals PUSH_RESUBSCRIBE_NEEDED.
 * Permission stays granted across iOS subscription drops, so this is silent.
 */
export function attachResubscribeListener(): () => void {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return () => {};
  }
  const handler = (event: MessageEvent) => {
    if (event.data?.type === "PUSH_RESUBSCRIBE_NEEDED") {
      if (typeof Notification === "undefined") return;
      if (Notification.permission === "granted") {
        void subscribeToPush();
      }
    }
  };
  navigator.serviceWorker.addEventListener("message", handler);
  return () => navigator.serviceWorker.removeEventListener("message", handler);
}
